import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import FrostedGlass from '@cartwhl/ui/utilities/FrostedGlass';
import {
  PANEL_WRITING_CLASSNAME,
  PANEL_WRITING_VARIANT,
} from '../../presentations/panelWritingStyle';

export const FLOATING_PANEL_COLLAPSED_WIDTH = 176;
const COLLAPSED_HEIGHT = 42;
const PANEL_ANIMATION_DURATION_CLASS = 'duration-200';
const PANEL_ANIMATION_EASE_CLASS = 'ease-out';
const DEFAULT_MIN_WIDTH = 260;
const DEFAULT_MAX_WIDTH = 520;
const DEFAULT_MIN_HEIGHT = 300;
const DEFAULT_MAX_HEIGHT = Number.POSITIVE_INFINITY;
const PANEL_HEADER_INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, summary, [role="button"], [role="link"], [data-panel-toggle-ignore="true"]';

interface FloatingPanelProps {
  title: string;
  titleClassName?: string;
  headerContent?: ReactNode;
  side: 'left' | 'right';
  width: number;
  height: number;
  isOpen: boolean;
  onToggleOpen: () => void;
  onWidthChange: (width: number) => void;
  onHeightChange: (height: number) => void;
  children: ReactNode;
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  className?: string;
  actions?: ReactNode;
  footerContent?: ReactNode;
  showResizeHandle?: boolean;
  isTemporarilyHidden?: boolean;
}

interface ResizeState {
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export default function FloatingPanel({
  title,
  titleClassName,
  headerContent,
  side,
  width,
  height,
  isOpen,
  onToggleOpen,
  onWidthChange,
  onHeightChange,
  children,
  minWidth = DEFAULT_MIN_WIDTH,
  maxWidth = DEFAULT_MAX_WIDTH,
  minHeight = DEFAULT_MIN_HEIGHT,
  maxHeight = DEFAULT_MAX_HEIGHT,
  className = '',
  actions,
  footerContent,
  showResizeHandle = true,
  isTemporarilyHidden = false,
}: FloatingPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeStateRef = useRef<ResizeState | null>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  const getMaxWidth = useCallback(() => {
    if (typeof window === 'undefined') return maxWidth;
    return Math.min(maxWidth, Math.max(minWidth, window.innerWidth - 72));
  }, [maxWidth, minWidth]);

  const getAvailableHeight = useCallback(() => {
    const parentElement = containerRef.current?.parentElement;
    if (parentElement) {
      return Math.floor(parentElement.getBoundingClientRect().height);
    }
    if (typeof window === 'undefined') return minHeight;
    return Math.floor(window.innerHeight);
  }, [minHeight]);

  const getMaxHeight = useCallback(() => {
    const availableHeight = getAvailableHeight();
    return Math.min(maxHeight, Math.max(minHeight, availableHeight));
  }, [getAvailableHeight, maxHeight, minHeight]);

  const handleResizeStart = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!isOpen) return;
      event.preventDefault();
      event.stopPropagation();
      resizeStateRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        startWidth: width,
        startHeight: height,
      };
      setIsResizing(true);
    },
    [height, isOpen, width],
  );

  const clampPanelSize = useCallback(() => {
    if (!isOpen) return;
    const clampedWidth = clamp(width, minWidth, getMaxWidth());
    const clampedHeight = clamp(height, minHeight, getMaxHeight());

    if (clampedWidth !== width) onWidthChange(clampedWidth);
    if (clampedHeight !== height) onHeightChange(clampedHeight);
  }, [
    getMaxHeight,
    getMaxWidth,
    height,
    isOpen,
    minHeight,
    minWidth,
    onHeightChange,
    onWidthChange,
    width,
  ]);

  useEffect(() => {
    clampPanelSize();
    window.addEventListener('resize', clampPanelSize);
    return () => window.removeEventListener('resize', clampPanelSize);
  }, [clampPanelSize]);

  useEffect(() => {
    const parentElement = containerRef.current?.parentElement;
    if (!parentElement || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(clampPanelSize);
    observer.observe(parentElement);
    return () => observer.disconnect();
  }, [clampPanelSize]);

  const updateTitleTruncation = useCallback(() => {
    const titleElement = titleRef.current;
    if (!titleElement) return;
    const nextIsTruncated = titleElement.scrollWidth > titleElement.clientWidth + 1;
    setIsTitleTruncated((prev) =>
      prev === nextIsTruncated ? prev : nextIsTruncated,
    );
  }, []);

  useEffect(() => {
    updateTitleTruncation();
  }, [title, updateTitleTruncation]);

  useEffect(() => {
    const titleElement = titleRef.current;
    if (!titleElement || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateTitleTruncation);
    observer.observe(titleElement);
    return () => observer.disconnect();
  }, [updateTitleTruncation]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (event: MouseEvent) => {
      const resizeState = resizeStateRef.current;
      if (!resizeState) return;

      const deltaX = event.clientX - resizeState.startX;
      const deltaY = event.clientY - resizeState.startY;
      const directionalDelta = side === 'left' ? deltaX : -deltaX;
      const nextWidth = clamp(
        resizeState.startWidth + directionalDelta,
        minWidth,
        getMaxWidth(),
      );
      const nextHeight = clamp(
        resizeState.startHeight + deltaY,
        minHeight,
        getMaxHeight(),
      );
      onWidthChange(nextWidth);
      onHeightChange(nextHeight);
    };

    const handleMouseUp = () => {
      resizeStateRef.current = null;
      setIsResizing(false);
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor =
      side === 'left' ? 'nwse-resize' : 'nesw-resize';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    getMaxHeight,
    getMaxWidth,
    isResizing,
    minHeight,
    minWidth,
    onHeightChange,
    onWidthChange,
    side,
  ]);

  const outerPositionClass = side === 'left' ? 'left-0' : 'right-0';
  const resizeHandleClass = side === 'left' ? 'right-2' : 'left-2';
  const footerPositionClass = side === 'left' ? 'left-2' : 'right-2';
  const resizeCursorClass =
    side === 'left' ? 'cursor-nwse-resize' : 'cursor-nesw-resize';
  const panelWidth = isOpen ? width : FLOATING_PANEL_COLLAPSED_WIDTH;
  const panelHeight = isOpen ? height : COLLAPSED_HEIGHT;
  const contentMaxHeight = isOpen ? Math.max(0, height - COLLAPSED_HEIGHT) : 0;
  const panelSizeMotionClass = isResizing
    ? ''
    : `transition-[width,height] ${PANEL_ANIMATION_DURATION_CLASS} ${PANEL_ANIMATION_EASE_CLASS}`;
  const contentMotionClass = isResizing
    ? ''
    : `transition-[max-height,opacity] ${PANEL_ANIMATION_DURATION_CLASS} ${PANEL_ANIMATION_EASE_CLASS}`;
  const visibilityClassName = isTemporarilyHidden
    ? 'opacity-0 pointer-events-none'
    : 'opacity-100 pointer-events-auto';
  const panelSurfaceClassName = isOpen
    ? 'dark:bg-gray-90/40 shadow-menu bg-gray-15/45 overflow-hidden rounded-2xl'
    : 'dark:bg-gray-90/40 bg-gray-15/45 overflow-hidden rounded-2xl backdrop-blur-md';
  const headerClassName = `border-gray-30/70 dark:border-gray-60/70 relative flex h-10 flex-none items-center justify-center px-3 cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-50/80 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent ${
    isOpen
      ? 'border-b'
      : 'h-[30px] rounded-lg bg-gray-15/25 text-gray-90 dark:bg-gray-90/25 dark:text-gray-15 hover:bg-gray-15 dark:hover:bg-gray-90'
  }`;
  const canToggleFromHeaderTarget = useCallback(
    (target: EventTarget | null, currentTarget: HTMLElement) => {
      if (!(target instanceof HTMLElement)) return true;
      const interactiveElement = target.closest(PANEL_HEADER_INTERACTIVE_SELECTOR);
      return !interactiveElement || interactiveElement === currentTarget;
    },
    [],
  );
  const handleHeaderClick = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (!canToggleFromHeaderTarget(event.target, event.currentTarget)) return;
      onToggleOpen();
    },
    [canToggleFromHeaderTarget, onToggleOpen],
  );
  const handleHeaderKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (!canToggleFromHeaderTarget(event.target, event.currentTarget)) return;
      event.preventDefault();
      onToggleOpen();
    },
    [canToggleFromHeaderTarget, onToggleOpen],
  );

  const panelBody = (
    <div
      className={`relative flex h-full flex-col ${panelSizeMotionClass} ${isOpen ? '' : 'p-1.5'} ${className}`}
      style={{
        width: panelWidth,
        height: panelHeight,
      }}
    >
      <header
        className={headerClassName}
        onClick={handleHeaderClick}
        onKeyDown={handleHeaderKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${title}`}
      >
        {headerContent ? (
          <div className="min-w-0 flex-1">{headerContent}</div>
        ) : (
          <Tooltip
            text={title}
            side={side === 'left' ? 'right' : 'left'}
            inModal
            disabled={!isTitleTruncated}
          >
            <span ref={titleRef} className="block max-w-[70%]">
              <Text
                variant={PANEL_WRITING_VARIANT}
                className={`block truncate text-center ${titleClassName ?? PANEL_WRITING_CLASSNAME}`}
              >
                {title}
              </Text>
            </span>
          </Tooltip>
        )}
        {actions && (
          <div
            className="absolute right-3 flex flex-none items-center gap-1"
            data-panel-toggle-ignore="true"
          >
            {actions}
          </div>
        )}
      </header>

      <div
        className={`min-h-0 flex-1 overflow-hidden ${contentMotionClass} ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        style={{ maxHeight: contentMaxHeight }}
      >
        {children}
      </div>

      {isOpen && footerContent && (
        <div className={`absolute bottom-2 ${footerPositionClass}`}>
          {footerContent}
        </div>
      )}

      {isOpen && showResizeHandle && (
        <button
          type="button"
          aria-label={`Resize ${title} panel width and height`}
          onMouseDown={handleResizeStart}
          className={`hover:bg-gray-15 dark:hover:bg-gray-75 absolute bottom-2 flex h-5 w-5 items-center justify-center rounded-md ${resizeCursorClass} ${resizeHandleClass}`}
        >
          <span className="pointer-events-none grid grid-cols-3 gap-[2px]">
            {Array.from({ length: 9 }).map((_, index) => (
              <span
                key={index}
                className="bg-gray-45 dark:bg-gray-30 h-[2px] w-[2px] rounded-full opacity-80"
              />
            ))}
          </span>
        </button>
      )}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={`absolute top-0 ${outerPositionClass} transition-opacity duration-200 ${visibilityClassName}`}
    >
      {isOpen ? (
        <FrostedGlass className={panelSurfaceClassName}>{panelBody}</FrostedGlass>
      ) : (
        <div className={panelSurfaceClassName}>{panelBody}</div>
      )}
    </div>
  );
}
